import { 
    JanusClientOptions, 
    JanusApiError, 
    Options, 
    InternalOptions,
    AuthFailureResponse,
    AuthResponseDto,
    User,
} from "./types";
import { cleanUndefined } from "./utils";
import { AuthClient } from "./clients/auth.client";
import { DataClient } from "./clients/data.client";
import { AdminClient } from "./clients/admin.client";
import { LogsClient } from './clients/logs.client';
import { AiClient } from './clients/ai.client';
import { ChatClient } from './clients/chat.client';
import { resolveEndpoint } from "./utils/endpoint-resolver";
// Remove node-fetch import, rely on globalThis.fetch and its types
// import FormData from 'form-data'; // REMOVED: Keep for instanceof check

/**
 * Janus V2 API Client
 * Provides access to the Janus V2 backend services.
 */
export class JanusAPIv2Client {
    // --- Configuration ---
    private readonly baseUrl: string;
    private readonly options: InternalOptions;
    private token: string | null = null;

    // --- Namespaced Clients --- Access backend features via these properties ---
    public readonly auth: AuthClient;
    public readonly data: DataClient;
    public readonly admin: AdminClient;
    public readonly logs: LogsClient;
    public readonly ai: AiClient;
    public readonly chat: ChatClient;
    // Example:
    // public readonly admin = new AdminClient(this._request.bind(this));
    // ...etc

    /**
     * Creates an instance of JanusAPIv2Client.
     * @param baseUrl The base URL of the Janus V2 API server (e.g., "http://localhost:3000/api/v2").
     * @param options Configuration options for the client.
     */
    constructor(baseUrl: string, options: Partial<JanusClientOptions> = {}) {
        this.baseUrl = baseUrl.replace(/\/+$/, ''); // Remove trailing slash
        this.options = {
             // Use globalThis.fetch if no fetch provided in options
            fetch: (options.fetch || globalThis.fetch) as typeof globalThis.fetch,
            timeout: options.timeout || 30000,
            ...options,
        } as InternalOptions;
        
        if (!this.options.fetch) {
             throw new Error(
                 'Fetch API is not available globally and was not provided in options.'
             );
         }

        // Initialize namespaced clients
        this.auth = new AuthClient(this._request.bind(this), this.setToken.bind(this));
        this.data = new DataClient(this);
        this.admin = new AdminClient(this);
        this.logs = new LogsClient(this);
        this.ai = new AiClient(this);
        this.chat = new ChatClient(this.baseUrl);
        // Initialize other clients here...

    }

    /**
     * Returns the resolved base URL this client is pinned to.
     * Useful when baseUrlCandidates were provided to the async factory.
     */
    getResolvedBaseUrl(): string {
        return this.baseUrl;
    }

    /**
     * Async factory that can resolve baseUrl from candidate endpoints before constructing the client.
     */
    static async create(options: Partial<JanusClientOptions> & { baseUrl?: string; baseUrlCandidates?: Array<string | { url: string; priority?: number }> } = {}): Promise<JanusAPIv2Client> {
        const {
            baseUrl,
            baseUrlCandidates,
            endpointProbeTimeoutMs,
            cacheResolvedEndpoint,
            token,
            ...rest
        } = options;

        const candidates = (baseUrlCandidates ?? []).map(candidate =>
            typeof candidate === 'string' ? { url: candidate } : candidate,
        ).filter(c => !!c.url);

        let resolvedBaseUrl = baseUrl;

        if (candidates.length > 0) {
            const resolved = await resolveEndpoint(candidates, {
                fetchImpl: (rest.fetch || globalThis.fetch) as typeof fetch,
                timeoutMs: endpointProbeTimeoutMs ?? 1000,
                cache: cacheResolvedEndpoint !== false,
            });
            resolvedBaseUrl = resolved.url;
        }

        if (!resolvedBaseUrl) {
            throw new Error('A baseUrl (or baseUrlCandidates) is required to create JanusAPIv2Client');
        }

        const client = new JanusAPIv2Client(resolvedBaseUrl, { ...rest, token });
        if (token) {
            client.setToken(token);
        }
        return client;
    }

    // --- Core Request Logic --- (Internal method used by sub-clients) ---

    /**
     * @internal
     * Makes a request to the Janus API.
     * This method is intended for internal use by the namespaced client classes.
     */
    async _request<T = any>(
        method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
        path: string,
        options: Options = {}
    ): Promise<T> {
        const url = `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.options.timeout);

        // Use global Headers type, cast input to any to bypass conflict
        const headers = new Headers(options.headers as HeadersInit || {});
        if (this.token) {
            headers.set('Authorization', `Bearer ${this.token}`);
        }
        // Use global FormData for instanceof check
        if (!options.skipContentTypeHeader && !headers.has('Content-Type') && options.body && !(options.body instanceof globalThis.FormData)) {
             headers.set('Content-Type', 'application/json');
        }
        if (!headers.has('Accept')) {
           headers.set('Accept', 'application/json');
        }

        // Use global BodyInit type
        let body: BodyInit | null | undefined = undefined;
        if (options.body) {
            // Check against global FormData
            if (options.body instanceof globalThis.FormData) {
                body = options.body as FormData;
            } else if (options.body instanceof Blob) {
                // Handle Blob differently for Node.js vs browser
                if (typeof window === 'undefined') {
                    // Node.js environment - convert Blob to Buffer for node-fetch compatibility
                    try {
                        const arrayBuffer = await options.body.arrayBuffer();
                        // Create Buffer without direct reference to avoid bundling issues
                        const BufferConstructor = (globalThis as any).Buffer || require('buffer').Buffer;
                        body = BufferConstructor.from(arrayBuffer);
                    } catch (error) {
                        // Fallback: try to get text and convert
                        try {
                            const text = await options.body.text();
                            const BufferConstructor = (globalThis as any).Buffer || require('buffer').Buffer;
                            body = BufferConstructor.from(text, 'utf8');
                        } catch (fallbackError) {
                            // Final fallback: pass Blob directly and hope for the best
                            body = options.body as BodyInit;
                        }
                    }
                } else {
                    // Browser environment - use Blob directly
                    body = options.body as BodyInit;
                }
            } else if (options.body instanceof ArrayBuffer ||
                       typeof options.body === 'string') {
                // Handle ArrayBuffer and string directly
                body = options.body as BodyInit;
            } else if (typeof options.body === 'object' && options.body !== null && 
                       typeof (options.body as any).constructor !== 'undefined' &&
                       (options.body as any).constructor.name === 'Buffer') {
                // Handle Node.js Buffer (check by constructor name to avoid direct Buffer reference)
                body = options.body as BodyInit;
            } else if (typeof options.body === 'object' && options.body !== null) {
                // JSON stringify for plain objects
                body = JSON.stringify(options.body);
            } else {
                // Fallback for other types
                body = options.body as BodyInit;
            }
        }

        // Handle query parameters
        const urlObject = new URL(url);
        if (options.params) {
            Object.entries(options.params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                   urlObject.searchParams.append(key, String(value));
                }
            });
        }
        
        try {
            // Use global RequestInit type
            const fetchOptions: RequestInit = {
                 method,
                 headers,
                 body,
                 signal: controller.signal,
            };
            // Fetch implementation should match global fetch signature
            // Assume this.options.fetch matches global fetch signature
            const response: Response = await this.options.fetch!(urlObject.toString(), fetchOptions);

            clearTimeout(timeoutId);

            if (!response.ok) {
                let errorBody: any;
                try {
                    errorBody = await response.json();
                } catch (e) {
                    try {
                       errorBody = await response.text(); 
                    } catch (textErr) {
                        errorBody = `Status: ${response.statusText}`; // Fallback if text fails
                    }
                }
                
                // Extract meaningful error message from response body
                let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
                if (typeof errorBody === 'object' && errorBody !== null) {
                    if (errorBody.message) {
                        errorMessage += ` - ${errorBody.message}`;
                    } else if (errorBody.error) {
                        errorMessage += ` - ${errorBody.error}`;
                    } else {
                        // Fallback: try to extract useful info from object or stringify it
                        try {
                            const bodyStr = JSON.stringify(errorBody);
                            if (bodyStr !== '{}') {
                                errorMessage += ` - ${bodyStr}`;
                            }
                        } catch (e) {
                            // If JSON.stringify fails, don't append anything
                        }
                    }
                } else if (typeof errorBody === 'string') {
                    errorMessage += ` - ${errorBody}`;
                }
                
                throw new JanusApiError(
                    errorMessage,
                    response.status,
                    errorBody,
                    response // Pass Response (now DOM type)
                );
            }

            // Now check response types using known methods
            if (options.responseType === 'blob') {
                 // Assume response has a blob() method compatible with Blob
                 return response.blob() as Promise<T>; 
             }
            if (options.responseType === 'text') {
                 return response.text() as Promise<T>;
            }
            // Check content-length or status for empty body before assuming json()
            const contentLength = response.headers?.get('content-length');
            if (response.status === 204 || (contentLength && parseInt(contentLength, 10) === 0)) {
                 return Promise.resolve({} as T); 
            }
            // Default to JSON
             return response.json() as Promise<T>;

        } catch (error: unknown) {
            clearTimeout(timeoutId);
            if (error instanceof JanusApiError) {
                throw error;
            } else if (error instanceof Error && error.name === 'AbortError') {
                throw new JanusApiError('Request timed out', 408);
            } else if (error instanceof Error) {
                 throw new JanusApiError(`Network or other error: ${error.message}`, 500, error);
            } else {
                throw new JanusApiError(`An unknown error occurred`, 500, error);
            }
        }
    }

    // --- Authentication Handling ---

    /**
     * Sets the authentication token for subsequent requests.
     * Triggers the onTokenUpdate callback if provided.
     * @param token The JWT token, or null to clear authentication.
     */
    setToken(token: string | null): void {
        const previousToken = this.token;
        this.token = token;
        
        // Call onTokenUpdate callback if token changed and callback is provided
        if (previousToken !== token && this.options.onTokenUpdate) {
            this.options.onTokenUpdate(token);
        }
    }

    /**
     * Gets the current authentication token.
     * @returns The current JWT token, or null if not authenticated.
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Checks if the client currently has an authentication token set.
     * @returns True if authenticated, false otherwise.
     */
    isAuthenticated(): boolean {
        return !!this.token;
    }

    /**
     * Validates if the current token is properly formatted and not expired.
     * Note: This only checks format and expiration, not server-side validity.
     * @returns True if token appears valid, false otherwise.
     */
    isTokenValid(): boolean {
        if (!this.token) return false;
        
        try {
            const parts = this.token.split('.');
            if (parts.length !== 3 || !parts[1]) return false;
            
            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            
            // Check expiration
            if (payload.exp) {
                const now = Math.floor(Date.now() / 1000);
                return payload.exp > now;
            }
            
            // If no expiration, consider it valid (format-wise)
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Gets the expiration date of the current token.
     * @returns The expiration date, or null if no token or no expiration.
     */
    getTokenExpiration(): Date | null {
        if (!this.token) return null;
        
        try {
            const parts = this.token.split('.');
            if (parts.length !== 3 || !parts[1]) return null;
            
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            
            if (payload.exp) {
                return new Date(payload.exp * 1000);
            }
            
            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Gets comprehensive information about the current token.
     * @returns Token information object.
     */
    getTokenInfo(): { token: string | null; isValid: boolean; expiresAt?: Date } {
        return {
            token: this.token,
            isValid: this.isTokenValid(),
            expiresAt: this.getTokenExpiration() || undefined
        };
    }

    /**
     * Synchronizes the current token to localStorage.
     * Useful for persisting authentication across browser sessions.
     * @param storageKey The localStorage key to use (defaults to 'janus_auth_token').
     * @returns True if successfully stored, false otherwise.
     */
    syncTokenToStorage(storageKey: string = 'janus_auth_token'): boolean {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false; // Not in browser environment
        }
        
        try {
            if (this.token) {
                localStorage.setItem(storageKey, this.token);
            } else {
                localStorage.removeItem(storageKey);
            }
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Loads a token from localStorage and sets it as the current token.
     * @param storageKey The localStorage key to read from (defaults to 'janus_auth_token').
     * @returns True if successfully loaded and set, false otherwise.
     */
    syncTokenFromStorage(storageKey: string = 'janus_auth_token'): boolean {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false; // Not in browser environment
        }
        
        try {
            const storedToken = localStorage.getItem(storageKey);
            if (storedToken) {
                this.setToken(storedToken);
                return true;
            } else {
                this.setToken(null);
                return false;
            }
        } catch (error) {
            return false;
        }
    }

    /**
     * Registers a callback to be called whenever the authentication state changes.
     * @param callback Function to call when authentication state changes.
     * @returns A function to unregister the callback.
     */
    onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void {
        // Store the original onTokenUpdate callback
        const originalCallback = this.options.onTokenUpdate;
        
        // Create a new callback that calls both the original and the new one
        this.options.onTokenUpdate = (token: string | null) => {
            // Call original callback if it exists
            if (originalCallback) {
                originalCallback(token);
            }
            
            // Call the auth state change callback
            callback(!!token);
        };
        
        // Return unregister function
        return () => {
            this.options.onTokenUpdate = originalCallback;
        };
    }

    /**
     * Refreshes the token if it's close to expiration.
     * Note: This requires the user to be authenticated with credentials that support refresh.
     * Currently, this is a placeholder - actual refresh logic would depend on backend support.
     * @param thresholdMinutes Refresh if token expires within this many minutes (default: 5).
     * @returns True if refresh was attempted/successful, false if not needed or failed.
     */
    async refreshTokenIfNeeded(thresholdMinutes: number = 5): Promise<boolean> {
        const tokenInfo = this.getTokenInfo();
        
        if (!tokenInfo.isValid || !tokenInfo.expiresAt) {
            return false; // No valid token or no expiration info
        }
        
        const now = new Date();
        const thresholdTime = new Date(now.getTime() + (thresholdMinutes * 60 * 1000));
        
        if (tokenInfo.expiresAt <= thresholdTime) {
            // Token is close to expiration - attempt refresh
            // Note: This is a placeholder. Actual implementation would depend on
            // backend refresh token support or re-authentication with stored credentials
            return false;
        }
        
        return true; // Token is still valid
    }

    // --- Convenience Methods (Optional) ---
    // You might add high-level methods here that combine calls from multiple clients
    // e.g., client.uploadAndGetData(zone, path, file)
} 